/**
 * UI-telemetry helper — fire-and-forget POST to /api/v1/telemetry/events.
 *
 * Plan reference: /root/.claude/plans/lexical-pondering-stonebraker.md
 *                 §"Verification" — 12 metrics land in ui_telemetry_events.
 *
 * Contract:
 *   - PII-free. Never pass message text, emails, names, prompts.
 *   - Allow-listed event_type — see app/api/v1/telemetry.py for the
 *     server-side enum.
 *   - Failures are swallowed; this never blocks UI.
 *
 * Usage:
 *
 *   import { recordTelemetry } from "@/lib/telemetry";
 *
 *   <button
 *     onClick={() => {
 *       recordTelemetry("felix_pill_click", { panel_open: open });
 *       toggle();
 *     }}
 *   />
 */

import { apiClient } from "@/api/client";

// Mirror of app/api/v1/telemetry.py:_ALLOWED_EVENT_TYPES. Keep in sync.
// TypeScript narrows the slug at the call site so a typo fails at compile.
//
// Three retired entries (2026-05-28 enum cleanup, second-pass review):
//   * `felix_fab_legacy_click` — the legacy FelixLauncher FAB was
//     deleted in PR #828 (single-icon cutover). The event still
//     appears in the server allow-list for historical-data
//     compatibility but no production component emits it anymore.
//   * `home_pane_dismissed` — the Home AttentionPanel panes are not
//     dismissible by users; the event was speculative and is removed
//     until a dismiss affordance ships.
//   * `status_explanation_action_clicked` — depends on an "Run this
//     recommendation" button inside ExplanationBody that doesn't
//     exist yet; will re-add when that affordance lands.
//
// One entry kept-but-unemittable:
//   * `felix_open_via_shortcut` — Felix has no global keyboard
//     shortcut today (⌘K is owned by CommandPalette). Left in the
//     enum so when a Felix shortcut lands, the event slug is stable.
export type TelemetryEventType =
  | "attention_item_clicked"
  | "delta_strip_engaged"
  | "card_to_detail"
  | "lineage_expanded"
  | "status_explanation_hover"
  | "hierarchy_breadcrumb_clicked"
  | "program_project_attached"
  | "briefing_action_clicked"
  | "briefing_read_through"
  | "density_toggled"
  | "ui_mode_switched" // universal Classic/Frontier posture switch
  | "felix_pill_click"
  | "felix_open_via_shortcut" // reserved for future use
  | "next_best_action_previewed"
  | "next_best_action_executed"
  // V3 Phase 1 events (briefing-first landing + 6-mode selector)
  | "ftl_ux_v3.briefing_landing_loaded"
  | "ftl_ux_v3.project_card_clicked"
  | "ftl_ux_v3.ranked_home_loaded"
  | "ftl_ux_v3.ranked_home_item_open"
  | "ftl_ux_v3.ranked_home_ask_felix"
  | "ftl_ux_v3.command_center_loaded"
  | "ftl_ux_v3.project_command_center_ask_felix"
  | "ftl_ux_v3.mode_inferred"
  | "ftl_ux_v3.mode_overridden"
  | "ftl_ux_v3.legacy_route_used"
  | "ftl_ux_v3.briefing_load_failed"
  // V3 Phase 2 events (Iterator showcase — designer-tunable backend surface)
  | "v3.iterator_showcase.start_session"
  | "v3.iterator_showcase.append_transcript"
  | "v3.iterator_showcase.commit_iteration"
  | "v3.iterator_showcase.end_session"
  // V3 Phase 3 events (Iterator MVP frontend — fullscreen route).
  // Mirrors `ftl.ux.v3.iterator.*` server allow-list in
  // app/api/v1/telemetry.py. All payloads PII-free.
  | "ftl.ux.v3.iterator.route_mounted"
  | "ftl.ux.v3.iterator.session_opened"
  | "ftl.ux.v3.iterator.session_resumed"
  | "ftl.ux.v3.iterator.transcript_sent"
  | "ftl.ux.v3.iterator.iterate_clicked"
  | "ftl.ux.v3.iterator.iteration_loaded"
  | "ftl.ux.v3.iterator.markup_created"
  | "ftl.ux.v3.iterator.conceptual_html_accepted"
  | "ftl.ux.v3.iterator.conceptual_html_rejected"
  | "ftl.ux.v3.iterator.sse_connected"
  | "ftl.ux.v3.iterator.sse_reconnected"
  | "ftl.ux.v3.iterator.sse_disconnected"
  | "ftl.ux.v3.iterator.flag_gated_blocked"
  | "ftl.ux.v3.iterator.voice_started"
  // V3 Phase 4 events (CrystallizationKernel — Patent J singleton).
  // V3 Phase 2.5 — Iterator Workflow Engine (Patent Candidate AB).
  // Mirrors the workflow_* slugs in app/api/v1/telemetry.py. PII-free.
  | "ftl.ux.v3.iterator.workflow_planned"
  | "ftl.ux.v3.iterator.workflow_completed"
  | "ftl.ux.v3.iterator.workflow_aborted"
  | "ftl.ux.v3.iterator.workflow_step_failed"

  // Mirrors `ftl.ux.v3.crystallization.*` server allow-list in
  // app/api/v1/telemetry.py. All payloads PII-free; stage / counts /
  // booleans only.
  | "ftl.ux.v3.crystallization.kernel_mounted"
  | "ftl.ux.v3.crystallization.stage_transitioned"
  | "ftl.ux.v3.crystallization.iteration_frozen"
  | "ftl.ux.v3.crystallization.node_clicked"
  | "ftl.ux.v3.crystallization.cluster_expanded"
  | "ftl.ux.v3.crystallization.particle_budget_clamped"
  | "ftl.ux.v3.crystallization.showcase_mounted"
  | "ftl.ux.v3.crystallization.showcase_param_changed"
  // V3 Phase 5 events (async fork-merge + meeting coupling + SMS).
  // Mirrors `iterator.*` server allow-list extensions in
  // app/api/v1/telemetry.py.
  | "iterator.fork_created"
  | "iterator.merge_proposed"
  | "iterator.merge_voted"
  | "iterator.merge_committed"
  | "iterator.merge_dissent_recorded"
  | "iterator.meeting_invite_scheduled"
  | "iterator.meeting_invite_dispatched"
  | "iterator.sms_opt_in_requested"
  | "iterator.sms_dispatched"
  // V3 Phase 5b — multi-user iterator UI events.
  // `iterator.mic_closed` fires when VoiceCapture stops the local mic
  // track (either PTT release or always-on toggle off). PII-free:
  // payload carries only session id + duration_ms. Mirrors the server
  // allow-list in app/api/v1/telemetry.py.
  | "iterator.mic_closed"
  // V3 Phase 6 — Shelf Sidebar (Patent Candidate I: Felix-driven
  // nav-surface state with Hover-with-Intent). PII-free; payload
  // carries category_id enum + source enum + numeric durations only.
  | "ftl.ux.v3.iterator.sidebar_category_opened"
  | "iterator.sidebar_shelf_hovered"
  | "iterator.sidebar_shelf_clicked"
  | "iterator.broadcast_shelf_clicked"
  | "iterator.sidebar_dismissed_by_iterator_mode"
  // V3 Phase 7 — UX-friction → auto-PR pipeline (Patent F mechanism).
  // The analyzer/auto-PR scripts emit these from the server; the
  // frontend mirror lets a future dashboard surface live signals.
  // PII-free: surface tags + class enums + numeric durations only.
  | "ftl.ux.v3.friction.signal_detected"
  | "ftl.ux.v3.friction.pr_opened"
  | "ftl.ux.v3.friction.pr_auto_merged"
  | "ftl.ux.v3.friction.pr_held_for_review"
  | "ftl.ux.v3.friction.loop_closed"
  // V3 Phase 9 — DomainSelector naming + checkout surface. Mirrors the
  // `ftl.ux.v3.domain.*` server allow-list in app/api/v1/telemetry.py.
  // PII-free: structural counts, term-model enum, availability booleans,
  // surface tags only — NO domain strings, NO registrant contact fields.
  | "ftl.ux.v3.domain.selector_mounted"
  | "ftl.ux.v3.domain.searched"
  | "ftl.ux.v3.domain.suggestions_loaded"
  | "ftl.ux.v3.domain.try_another_angle"
  | "ftl.ux.v3.domain.candidate_selected"
  | "ftl.ux.v3.domain.quote_loaded"
  | "ftl.ux.v3.domain.term_model_toggled"
  | "ftl.ux.v3.domain.purchase_started"
  | "ftl.ux.v3.domain.purchase_succeeded"
  | "ftl.ux.v3.domain.purchase_gated"
  | "ftl.ux.v3.domain.purchase_quote_expired"
  | "ftl.ux.v3.domain.purchase_already"
  | "ftl.ux.v3.domain.quote_refresh"
  | "ftl.ux.v3.domain.checkout_redirect"
  | "ftl.ux.v3.domain.flag_gated_blocked"
  // V3 Phase 10 — ProvisioningCanvas / tenant-hosting surface. Mirrors the
  // `ftl.ux.v3.hosting.*` server allow-list in app/api/v1/telemetry.py.
  // PII-free: a hosting_id (already an opaque UUID), a reduced-motion bool,
  // and a flag tag — NO IPs, NO fqdns, NO project names.
  | "ftl.ux.v3.hosting.canvas_viewed"
  | "ftl.ux.v3.hosting.shipped"
  | "ftl.ux.v3.hosting.bootstrap_failed"
  | "ftl.ux.v3.hosting.flag_gated_blocked"
  // Always-learning UX behavior analytics (#589). Raw human-interaction
  // signal aggregated by the nightly review into navigation funnels,
  // clicks-to-goal, abandonment, and Felix-vs-non-Felix cohort analysis.
  // STRICTLY PII-FREE: `path` is a TEMPLATE (/projects/:id), never literal
  // ids/queries; `target` is an element role + stable slug, never free text;
  // `felix_session` is a cohort bool. Mirrors app/api/v1/telemetry.py.
  | "ux.behavior.nav"
  | "ux.behavior.click"
  | "ux.behavior.dwell"
  | "ux.behavior.rage_click"
  | "ux.behavior.dead_end"
  | "ux.behavior.goal_reached"
  // First-run activation wizard funnel (concept->live). PII-FREE: step slug +
  // index + booleans only. Mirrors app/api/v1/telemetry.py.
  | "activation.wizard_started"
  | "activation.step_viewed"
  | "activation.step_completed"
  | "activation.step_skipped"
  | "activation.project_created"
  | "activation.domain_started"
  | "activation.team_invited"
  | "activation.felix_met"
  | "activation.completed"
  | "activation.dismissed";

/** PII-free payload values. The server scrubber drops anything that
 *  looks like message/email/prompt/etc, so prefer numeric durations,
 *  enum strings, and booleans here. */
export type TelemetryPayload = Record<
  string,
  string | number | boolean | null | undefined
>;

interface TelemetryRequest {
  event_type: TelemetryEventType;
  entity_type?: string | null;
  entity_id?: string | null;
  payload?: TelemetryPayload;
}

/**
 * Fire a telemetry event. Returns void; the network call is fire-
 * and-forget. Failures land in console.warn but never throw.
 *
 * The endpoint returns 202 Accepted — there's no value in awaiting it
 * from the UI's perspective, but the function still returns a promise
 * so tests can spy if they need to.
 */
export async function recordTelemetry(
  eventType: TelemetryEventType,
  payload?: TelemetryPayload,
  options?: { entityType?: string; entityId?: string },
): Promise<void> {
  const body: TelemetryRequest = {
    event_type: eventType,
    entity_type: options?.entityType ?? null,
    entity_id: options?.entityId ?? null,
    payload: payload ?? {},
  };
  try {
    // A background call: a refusal is logged here and never reaches Felix (row 81273755).
    await apiClient.post("/telemetry/events", body, { background: true });
  } catch (err) {
    // Telemetry must never break the UI. Log + swallow.
    // eslint-disable-next-line no-console
    console.warn("telemetry:", eventType, err);
  }
}
